/**
 * Геометрия грида и порядок слоёв. Менять согласованно — от этих констант зависят все
 * компоненты грида (шапка, строки, строка «Итог») и расчёт окна виртуализации.
 */
import { Theme, darken, lighten } from "@mui/material"

export const STAFF_COL_W = 160
export const DAY_COL_W = 64
export const TOTAL_COL_W = 92
export const ROW_H = 64

/** Ярусы шапки имеют РАЗНУЮ высоту, как на референсе: месяц, неделя, день недели, число. */
export const HEADER_TIER_H = [32, 32, 24, 24] as const
export const HEADER_H = HEADER_TIER_H.reduce((a, b) => a + b, 0)

/**
 * Порядок слоёв — обязательная часть схемы залипания (спека, раздел 8).
 * Без явных z-index уезжающие строки перекрывают шапку, а ячейки дней — залипшие колонки.
 * Залипшим ячейкам обязателен непрозрачный фон из темы и НИКОГДА opacity < 1:
 * opacity создала бы контекст наложения на элементе, который сам является слоем.
 */
export const Z = {
  header: 31,
  totalsRow: 30,
  stickyInHeader: 10, // углы внутри шапки и строки «Итог» (внутри своего контекста)
  stickyCol: 20, // залипшие колонки в обычных строках
  cell: 0,
} as const

export const gridWidth = (dayCount: number): number =>
  STAFF_COL_W + dayCount * DAY_COL_W + TOTAL_COL_W

/**
 * Подложка выходных — ОДНА формула для всех ярусов шапки, ячеек и строки «Итог»:
 * иначе колонка визуально разъезжается. Цвет непрозрачный (а не alpha), потому что
 * им красятся и залипшие элементы шапки/строки «Итог», где прозрачность недопустима.
 */
export const weekendBg = (theme: Theme): string =>
  theme.palette.mode === "light"
    ? darken(theme.palette.background.paper, 0.07)
    : lighten(theme.palette.background.paper, 0.07)

/**
 * Цвет линий грида. В светлой теме `palette.divider` — тёмно-красный (#7F0000):
 * на сетке из сотен линий он даёт «красную клетку» и перебивает цвета смен.
 * Поэтому здесь светло-серый; в тёмной теме divider и так серо-голубой — берём его.
 */
export const gridBorderColor = (theme: Theme): string =>
  theme.palette.mode === "light"
    ? theme.palette.grey[500]
    : theme.palette.divider

/** Усиленные линии: границы недель и выходных, отбивка залипших колонок и строки «Итог». */
export const gridBorderColorStrong = (theme: Theme): string =>
  theme.palette.mode === "light"
    ? theme.palette.grey[400]
    : theme.palette.divider

const isColumnSeparator = (weekday: number): boolean =>
  weekday === 1 || weekday === 6

/**
 * Левая граница колонки дня: двойной линией отделяются и начало недели (Пн),
 * и блок выходных (Сб). Вместе с 2px перед следующим Пн выходные обведены с двух
 * сторон — это видно и на закрашенных ячейках, где фон занят цветом смены.
 * Спред обязателен ПОСЛЕ borderColor — иначе тот перебьёт цвет левой границы.
 */
export const dayBorderLeftSx = (weekday: number) => ({
  borderLeft: isColumnSeparator(weekday) ? "2px solid" : "1px solid",
  borderLeftColor: isColumnSeparator(weekday)
    ? gridBorderColorStrong
    : gridBorderColor,
})

/** Толщина рамки активного мазка (и маркера «сегодня»). */
export const STROKE_BORDER_W = 2

export const ROW_OVERSCAN = 5
